/**
 * @file Onboarding Wizard — /onboarding
 *
 * 6-step wizard for new professional setup. Progress saved between steps.
 * Cosmetic skin ported from handoff-15. Operational logic — handlers,
 * hooks, server calls, validation — is untouched.
 */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Trash2,
  Copy,
  MessageCircle,
  Share2,
  Download,
  ExternalLink,
  LogOut,
  Video,
  MapPin,
  Eye,
  EyeOff,
  Calendar as CalendarIcon,
  DollarSign,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { useConsultationServices } from "../hooks/useConsultationServices";
import { useSchedule } from "../hooks/useSchedule";
import { useSpecialties } from "../hooks/useSpecialties";
import { professionalService } from "../services/professional";
import { subscriptionService } from "../services/subscription";
import {
  PROFESSIONAL_TITLES,
  PROFESSIONAL_STRINGS,
  SCHEDULE_STRINGS,
  SPANISH_COUNTRIES,
  MEXICO_STATES,
} from "../constants/strings";
import { ONBOARDING } from "../../../shared/onboarding-strings";
import { useAuth } from "../hooks/useAuth";
import { useProfessionalLocations } from "../hooks/useProfessionalLocations";
import { Spinner, toast } from "../ui";
import "./Onboarding.css";

const STEPS = ONBOARDING.STEPS;
const STEP_SHORT = ["Perfil", "Config", "Servicios", "Ubicación", "Horario", "Listo"];
const SPECIALTY_GROUPS = PROFESSIONAL_STRINGS.SPECIALTY_GROUPS;
const CURRENCIES = [
  "MXN", "COP", "ARS", "CLP", "PEN", "BOB", "PYG", "UYU",
  "CRC", "PAB", "GTQ", "HNL", "DOP", "SVC", "USD", "EUR",
];
const COUNTRY_CURRENCY = {
  MX: "MXN", CO: "COP", AR: "ARS", CL: "CLP", PE: "PEN",
  EC: "USD", BO: "BOB", PY: "PYG", UY: "UYU", CR: "CRC",
  PA: "PAB", GT: "GTQ", SV: "USD", HN: "HNL", DO: "DOP",
  PR: "USD", ES: "EUR",
};
const DAYS = SCHEDULE_STRINGS.DAYS.map((label, value) => ({ value, label }));
const BASE_URL = import.meta.env.VITE_PUBLIC_SITE_URL || "https://consultto.mx";
const LOCATION_LIMITS = { GRATIS: 1, PRO_TRIAL: 2, PRO: Infinity, PRO_PLUS: Infinity };

const getEffectiveTier = (profile) => {
  if (!profile) return "GRATIS";
  // PRO_PLUS is a retired plan — treat any legacy Pro+ record as Pro (unlimited).
  if (profile.plan === "PRO" || profile.plan === "PRO_PLUS") return "PRO";
  if (profile.proTrialRemaining > 0) return "PRO_TRIAL";
  return "GRATIS";
};

const buildSlug = (firstName, lastName, title = "") => {
  const prefix = title ? title.replace(/\./g, "") + " " : "";
  return `${prefix}${firstName} ${lastName}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
};

// ─── Reusable UI primitives ─────────────────────────────────────────────
function Field({ label, required, optional, error, hint, children }) {
  return (
    <div className="ob-field">
      <label className="ob-label">
        {label}
        {required && <span className="req">*</span>}
        {optional && <span className="opt">opcional</span>}
      </label>
      {children}
      {error ? (
        <div className="ob-help warn">{error}</div>
      ) : hint ? (
        <div className="ob-help">{hint}</div>
      ) : null}
    </div>
  );
}

function OBSelect({ value, onChange, error, children, ...rest }) {
  return (
    <div className="ob-select-wrap">
      <select
        className={"ob-select" + (error ? " err" : "")}
        value={value || ""}
        onChange={onChange}
        {...rest}
      >
        {children}
      </select>
    </div>
  );
}

function NumStep({ value, onChange, min = 0, max = 240, step = 5, unit = "min", error }) {
  const n = Number(value) || 0;
  return (
    <div className={"ob-stepper-num" + (error ? " err" : "")}>
      <div className="val">
        <span className="num">{value}</span>
        <span className="unit">{unit}</span>
      </div>
      <div className="btns">
        <button
          type="button"
          aria-label="Aumentar"
          onClick={() => onChange(Math.min(max, n + step))}
          disabled={n >= max}
        >
          +
        </button>
        <button
          type="button"
          aria-label="Disminuir"
          onClick={() => onChange(Math.max(min, n - step))}
          disabled={n <= min}
        >
          −
        </button>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <span className="mk">
      <i />
    </span>
  );
}

// ─── Main component ─────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser, logout } = useAuth();
  const { profile, isLoading, updateProfileSilent } = useProfessionalProfile();
  const { services, addService, removeService } = useConsultationServices();
  const { specialties, addSpecialty } = useSpecialties();
  const { locations, addLocation, removeLocation } = useProfessionalLocations();
  const { schedules, createSlot, deleteSlot } = useSchedule();
  const qrRef = useRef(null);

  useEffect(() => {
    const plan = searchParams.get("plan");
    if (plan) localStorage.setItem("pendingPlan", plan);
  }, [searchParams]);

  useEffect(() => {
    if (user?.onboardingComplete) navigate("/", { replace: true });
  }, [user?.onboardingComplete, navigate]);

  const [activeStep, setActiveStep] = useState(profile?.onboardingStep || 0);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [title, setTitle] = useState(profile?.title || "");
  const [specialty, setSpecialty] = useState(profile?.specialty || "");
  const [specialtyOther, setSpecialtyOther] = useState(profile?.specialtyOther || "");
  const [cedula, setCedula] = useState("");
  const [slug, setSlug] = useState(profile?.slug || "");
  const [slugError, setSlugError] = useState("");
  const [country, setCountry] = useState(profile?.country || "MX");

  const [sessionDuration, setSessionDuration] = useState(profile?.sessionDuration || 60);
  const [bufferTime, setBufferTime] = useState(profile?.bufferTime || 10);
  const [currency, setCurrency] = useState(profile?.currency || "MXN");
  const [publicPhone, setPublicPhone] = useState(profile?.publicPhone || "");
  const [publicEmail, setPublicEmail] = useState(profile?.publicEmail || "");

  const [svcName, setSvcName] = useState("");
  const [svcPrice, setSvcPrice] = useState("");
  const [svcSubmitting, setSvcSubmitting] = useState(false);

  const [locForm, setLocForm] = useState({ name: "", address: "", colonia: "", city: "", state: "" });
  const [locSubmitting, setLocSubmitting] = useState(false);
  const physicalLocationCount = locations.filter((l) => !l.isVirtual).length;
  const locationLimit = LOCATION_LIMITS[getEffectiveTier(profile)];
  const locationLimitReached = physicalLocationCount >= locationLimit;
  const canTryProTrial = profile?.proTrialRemaining === null;
  const teleEnabled = locations.some((l) => l.isVirtual);

  const [scheduleForm, setScheduleForm] = useState({
    dayOfWeek: 1,
    startTime: "09:00",
    endTime: "17:00",
    locationId: "",
  });
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Public profile visibility flags — registered data is still kept for
  // income tracking + slot generation; the toggles only hide the section
  // on the doctor's public /c/:slug profile.
  const [showPrices, setShowPrices] = useState(profile?.showPrices ?? true);
  const [showSchedule, setShowSchedule] = useState(profile?.showSchedule ?? true);

  // Sticky-top scrolled state
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!profile) return;
    setActiveStep(profile.onboardingStep || 0);
    setFirstName(profile.user?.firstName || "");
    setLastName(profile.user?.lastName || "");
    setTitle(profile.title || "");
    setSpecialty(profile.specialty || "");
    setSpecialtyOther(profile.specialtyOther || "");
    setSlug(profile.slug || "");
    setCountry(profile.country || "MX");
    setSessionDuration(profile.sessionDuration || 60);
    setBufferTime(profile.bufferTime || 10);
    setCurrency(profile.currency || "MXN");
    setShowPrices(profile.showPrices ?? true);
    setShowSchedule(profile.showSchedule ?? true);
    // If the user is past step 0, we know data is persisted server-side.
    if ((profile.onboardingStep || 0) > 0) setHasSavedOnce(true);
  }, [profile?.id]);

  useEffect(() => {
    if (!firstName || !lastName) return;
    setSlug(buildSlug(firstName, lastName, title));
  }, [title, firstName, lastName]);

  useEffect(() => {
    if (locations.length === 1 && !scheduleForm.locationId) {
      setScheduleForm((prev) => ({ ...prev, locationId: locations[0].id }));
    }
  }, [locations.length]); // eslint-disable-line

  const saveAndAdvance = async (data, nextStep) => {
    setSaving(true);
    try {
      const updated = await updateProfileSilent({ ...data, onboardingStep: nextStep });
      if (data.firstName || data.lastName) {
        setUser((prev) => ({
          ...prev,
          firstName: updated.user?.firstName || prev.firstName,
          lastName: updated.user?.lastName || prev.lastName,
        }));
      }
      setActiveStep(nextStep);
      setHasSavedOnce(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const validation = err?.response?.data?.validation;
      if (validation) {
        const segment = validation.body || validation.query || validation.params;
        const keys = segment?.keys || [];
        if (keys.length > 0) setFieldErrors((prev) => ({ ...prev, [keys[0]]: true }));
        toast.error(segment?.message || "Error de validación");
      } else {
        toast.error(err?.response?.data?.message || "Error al guardar. Intenta de nuevo.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStep0 = async () => {
    setFieldErrors({});
    if (!firstName.trim()) { setFieldErrors({ firstName: true }); return toast.error("El campo Nombre es obligatorio"); }
    if (!lastName.trim()) { setFieldErrors({ lastName: true }); return toast.error("El campo Apellido es obligatorio"); }
    if (!specialty) { setFieldErrors({ specialty: true }); return toast.error("Selecciona tu Especialidad"); }
    if (specialty === "OTRA" && !specialtyOther.trim()) { setFieldErrors({ specialtyOther: true }); return toast.error("Especifica tu Especialidad"); }
    if (!cedula.trim()) { setFieldErrors({ cedula: true }); return toast.error("El campo Cédula Profesional es obligatorio"); }
    if (!slug.trim()) { setFieldErrors({ slug: true }); return toast.error("El campo Enlace de Perfil no puede estar vacío"); }
    if (publicEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(publicEmail.trim())) {
      setFieldErrors({ publicEmail: true }); return toast.error("El Email Público no es válido");
    }

    if (slug !== profile.slug) {
      setSaving(true);
      try {
        await professionalService.updateSlug(slug.trim());
      } catch (err) {
        setSlugError(err.response?.data?.message || "Este enlace ya está en uso");
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    setSlugError("");

    const specialtyName = specialty === "OTRA"
      ? specialtyOther.trim()
      : (PROFESSIONAL_STRINGS.SPECIALTY_LABELS[specialty] || specialty);
    if (specialties.length === 0 && specialtyName) {
      try {
        await addSpecialty({ name: specialtyName, cedula: cedula.trim() || null });
      } catch {
        // non-blocking
      }
    }

    await saveAndAdvance({
      firstName: firstName.trim(), lastName: lastName.trim(),
      title, specialty, specialtyOther: specialtyOther || null,
      publicPhone: publicPhone.trim() || null, publicEmail: publicEmail.trim() || null,
    }, 1);
  };

  const handleStep1 = async () => {
    setFieldErrors({});
    if (!sessionDuration || Number(sessionDuration) < 15) { setFieldErrors({ sessionDuration: true }); return toast.error("La Duración de Sesión mínima es 15 minutos"); }
    if (Number(sessionDuration) > 480) { setFieldErrors({ sessionDuration: true }); return toast.error("La Duración de Sesión máxima es 480 minutos"); }
    if (bufferTime !== "" && (Number(bufferTime) < 0 || Number(bufferTime) > 120)) { setFieldErrors({ bufferTime: true }); return toast.error("El Tiempo entre Citas debe ser entre 0 y 120 minutos"); }
    await saveAndAdvance({
      sessionDuration: Number(sessionDuration), bufferTime: Number(bufferTime), currency, country,
    }, 2);
  };

  const handleStep2 = async () => {
    setFieldErrors({});
    if (svcName.trim() || svcPrice) {
      if (!svcName.trim()) { setFieldErrors({ svcName: true }); return toast.error("El campo Nombre del Servicio es obligatorio"); }
      if (!svcPrice || Number(svcPrice) <= 0) { setFieldErrors({ svcPrice: true }); return toast.error("El campo Precio debe ser mayor a 0"); }
      setSvcSubmitting(true);
      try {
        await addService({ name: svcName.trim(), price: Number(svcPrice) });
        setSvcName(""); setSvcPrice("");
      } catch {
        setSvcSubmitting(false); return;
      }
      setSvcSubmitting(false);
    } else if (services.length === 0) {
      return toast.error("Agrega al menos un servicio");
    }
    await saveAndAdvance({ showPrices }, 3);
  };

  const handleAddService = async () => {
    setFieldErrors({});
    if (!svcName.trim()) { setFieldErrors({ svcName: true }); return toast.error("El campo Nombre del Servicio es obligatorio"); }
    if (!svcPrice || Number(svcPrice) <= 0) { setFieldErrors({ svcPrice: true }); return toast.error("El campo Precio debe ser mayor a 0"); }
    setSvcSubmitting(true);
    try {
      await addService({ name: svcName.trim(), price: Number(svcPrice) });
      setSvcName(""); setSvcPrice("");
    } catch {
      // ignore
    } finally {
      setSvcSubmitting(false);
    }
  };

  const handleStep3 = async () => {
    setFieldErrors({});
    if (locForm.address.trim() || locForm.city.trim() || locForm.state.trim()) {
      if (!locForm.address.trim()) { setFieldErrors({ locAddress: true }); return toast.error("El campo Calle y Número es obligatorio"); }
      if (!locForm.city.trim()) { setFieldErrors({ locCity: true }); return toast.error("El campo Ciudad es obligatorio"); }
      if (!locForm.state.trim()) { setFieldErrors({ locState: true }); return toast.error("El campo Estado / Provincia es obligatorio"); }
      setLocSubmitting(true);
      try {
        await addLocation({ ...locForm, name: locForm.name || null, colonia: locForm.colonia || null });
        setLocForm({ name: "", address: "", colonia: "", city: "", state: "" });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Error al guardar la ubicación. Intenta de nuevo.");
        setLocSubmitting(false);
        return;
      }
      setLocSubmitting(false);
    } else if (locations.length === 0) {
      return toast.error("Agrega al menos una ubicación o teleconsulta");
    }
    await saveAndAdvance({}, 4);
  };

  const handleAddLocation = async (isVirtual) => {
    setFieldErrors({});
    if (!isVirtual && !locForm.address.trim()) { setFieldErrors({ locAddress: true }); return toast.error("El campo Calle y Número es obligatorio"); }
    if (!isVirtual && !locForm.city.trim()) { setFieldErrors({ locCity: true }); return toast.error("El campo Ciudad es obligatorio"); }
    if (!isVirtual && !locForm.state.trim()) { setFieldErrors({ locState: true }); return toast.error("El campo Estado / Provincia es obligatorio"); }
    setLocSubmitting(true);
    try {
      await addLocation(isVirtual
        ? { name: "Teleconsulta", isVirtual: true }
        : { ...locForm, name: locForm.name || null, colonia: locForm.colonia || null, city: locForm.city || null, state: locForm.state || null }
      );
      if (!isVirtual) setLocForm({ name: "", address: "", colonia: "", city: "", state: "" });
    } catch {
      // ignore
    } finally {
      setLocSubmitting(false);
    }
  };

  const handleToggleTele = async () => {
    if (teleEnabled) {
      const virtual = locations.find((l) => l.isVirtual);
      if (virtual) {
        try { await removeLocation(virtual.id); } catch {}
      }
    } else {
      await handleAddLocation(true);
    }
  };

  const handleStep4 = async () => {
    setFieldErrors({});
    if (schedules.length === 0) {
      if (scheduleForm.startTime >= scheduleForm.endTime) {
        setFieldErrors({ startTime: true, endTime: true });
        return toast.error("La Hora de Inicio debe ser antes que la Hora de Fin");
      }
      if (locations.length > 0 && !scheduleForm.locationId) {
        setFieldErrors({ locationId: true });
        return toast.error("Selecciona una Ubicación para este bloque de horario");
      }
      setScheduleSubmitting(true);
      try {
        await createSlot({ ...scheduleForm, locationId: scheduleForm.locationId || null });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Error al guardar el horario. Intenta de nuevo.");
        setScheduleSubmitting(false);
        return;
      }
      setScheduleSubmitting(false);
    }
    await saveAndAdvance({ showSchedule }, 5);
  };

  const handleAddSchedule = async () => {
    setFieldErrors({});
    if (scheduleForm.startTime >= scheduleForm.endTime) {
      setFieldErrors({ startTime: true, endTime: true });
      return toast.error("La Hora de Inicio debe ser antes que la Hora de Fin");
    }
    if (locations.length > 0 && !scheduleForm.locationId) {
      setFieldErrors({ locationId: true });
      return toast.error("Selecciona una Ubicación para este bloque de horario");
    }
    setScheduleSubmitting(true);
    try {
      await createSlot({ ...scheduleForm, locationId: scheduleForm.locationId || null });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error al guardar el horario. Intenta de nuevo.");
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await updateProfileSilent({ onboardingComplete: true, onboardingStep: 6 });
      setUser((prev) => ({ ...prev, onboardingComplete: true }));

      const pendingPlan = localStorage.getItem("pendingPlan");
      localStorage.removeItem("pendingPlan");

      if (pendingPlan === "trial") {
        try { await subscriptionService.activateTrial(); } catch {}
      }
      navigate("/", { replace: true });
    } catch {
      toast.error("Error al finalizar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const profileUrl = slug ? `${BASE_URL}/c/${slug}` : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("¡Enlace copiado!");
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`Agenda tu cita conmigo en Consultto: ${profileUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const handleFacebookShare = () =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`, "_blank");
  const handleTwitterShare = () => {
    const text = encodeURIComponent(`Agenda tu cita conmigo en Consultto: ${profileUrl}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };
  const handleInstagramShare = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("¡Enlace copiado! Pégalo en tu bio o historia de Instagram.");
    window.open("https://www.instagram.com", "_blank");
  };
  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-consultto-${slug}.png`;
    a.click();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const maxStepReached = profile?.onboardingStep ?? 0;

  if (isLoading) {
    return (
      <div className="ob-loading">
        <Spinner size={32} />
      </div>
    );
  }

  const stepHandlers = [handleStep0, handleStep1, handleStep2, handleStep3, handleStep4];
  const nextLabel = activeStep === 4 ? "Finalizar →" : "Continuar →";
  const isLast = activeStep === 5;
  const pct = Math.round(((activeStep + 1) / STEPS.length) * 100);

  return (
    <div className="cd-root ob-root" data-screen-label={`${String(activeStep + 1).padStart(2, "0")} ${STEP_SHORT[activeStep]}`}>
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div className={"ob-top" + (scrolled ? " scrolled" : "")}>
        <div className="ob-top-inner">
          <a className="ob-brand" href="#" onClick={(e) => e.preventDefault()}>
            <BrandMark />
            Consultto
          </a>
          <button className="ob-logout" type="button" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* ── Stepper ─────────────────────────────────────────────────── */}
      <div className="ob-stepper">
        <div className="ob-stepper-compact">
          <div className="row">
            <span className="cur">Paso {activeStep + 1} de {STEPS.length}</span>
            {activeStep + 1 < STEPS.length ? (
              <span className="next">Sigue · {STEP_SHORT[activeStep + 1]}</span>
            ) : (
              <span className="next">Final</span>
            )}
          </div>
          <div className="bar"><div className="fill" style={{ width: pct + "%" }} /></div>
          <div className="ttl">{STEPS[activeStep]}</div>
        </div>
        <div className="ob-stepper-pills">
          {STEPS.map((label, i) => {
            const completed = i < activeStep;
            const active = i === activeStep;
            const clickable = i <= maxStepReached;
            const cls = completed ? "done" : active ? "cur" : "fut";
            return (
              <button
                key={label}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && setActiveStep(i)}
                className={`ob-step ${cls}`}
              >
                <span className="dot">
                  {completed ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : i + 1}
                </span>
                <span className="lbl">{STEP_SHORT[i]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Page header (desktop only) ──────────────────────────────── */}
      <div className="ob-pagehead">
        <div className="eyebrow">Configura tu cuenta</div>
        <h1>Configura tu <em>perfil</em></h1>
      </div>

      {/* ── Main card ───────────────────────────────────────────────── */}
      <div className="ob-main">
        <div className="ob-card">
          <div className="ob-step-content" key={activeStep}>
            {/* ─── Step 0: Perfil público ─────────────────────────── */}
            {activeStep === 0 && (
              <>
                <div className="ob-card-head">
                  <h2>¿Cómo quieres <em>presentarte?</em></h2>
                  <p>Esta información aparecerá en tu perfil público para que tus pacientes te encuentren.</p>
                </div>

                <div className="ob-fields cols-12">
                  <Field label="Título">
                    <OBSelect value={title} onChange={(e) => setTitle(e.target.value)}>
                      <option value="">Sin título</option>
                      {PROFESSIONAL_TITLES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </OBSelect>
                  </Field>
                  <Field label="Nombre" required error={fieldErrors.firstName ? "Requerido" : undefined}>
                    <input
                      className={"ob-input" + (fieldErrors.firstName ? " err" : "")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Field>
                </div>
                <div className="ob-fields" style={{ marginTop: 14 }}>
                  <Field label="Apellidos" required error={fieldErrors.lastName ? "Requerido" : undefined}>
                    <input
                      className={"ob-input" + (fieldErrors.lastName ? " err" : "")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Field>
                  <Field label="Especialidad" required error={fieldErrors.specialty ? "Requerido" : undefined}>
                    <OBSelect
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                      error={fieldErrors.specialty}
                    >
                      <option value="">{ONBOARDING.SPECIALTY_PLACEHOLDER}</option>
                      {SPECIALTY_GROUPS.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.items.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </OBSelect>
                  </Field>
                  {specialty === "OTRA" && (
                    <Field label="Especifica tu especialidad" required error={fieldErrors.specialtyOther ? "Requerido" : undefined}>
                      <input
                        className={"ob-input" + (fieldErrors.specialtyOther ? " err" : "")}
                        value={specialtyOther}
                        onChange={(e) => setSpecialtyOther(e.target.value)}
                      />
                    </Field>
                  )}
                  <Field label="Cédula profesional" required hint={ONBOARDING.CEDULA_HELPER} error={fieldErrors.cedula ? "Requerido" : undefined}>
                    <input
                      className={"ob-input" + (fieldErrors.cedula ? " err" : "")}
                      placeholder="12345678"
                      value={cedula}
                      onChange={(e) => setCedula(e.target.value)}
                    />
                  </Field>
                </div>

                <div className="ob-section">
                  <div className="ob-section-title">{ONBOARDING.SLUG_SECTION_TITLE}</div>
                  <div className="ob-section-sub">{ONBOARDING.SLUG_HELPER}</div>
                  <div className={"ob-inputgroup" + ((slugError || fieldErrors.slug) ? " err" : "")}>
                    <span className="prefix">consultto.mx/c/</span>
                    <input
                      value={slug}
                      onChange={(e) => {
                        setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                        setSlugError("");
                      }}
                    />
                    {slug && !slugError ? (
                      <span className="suffix-status">
                        <span className="ck">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        </span>
                        libre
                      </span>
                    ) : null}
                  </div>
                  {slugError ? <div className="ob-help warn" style={{ marginTop: 6 }}>{slugError}</div> : null}
                </div>

                <div className="ob-section">
                  <div className="ob-section-title">{ONBOARDING.PUBLIC_CONTACT_HEADING}</div>
                  <div className="ob-section-sub">{ONBOARDING.PUBLIC_CONTACT_HELPER}</div>
                  <div className="ob-fields cols-2">
                    <Field label={ONBOARDING.PUBLIC_PHONE_LABEL} optional>
                      <input
                        className="ob-input"
                        placeholder={ONBOARDING.PUBLIC_PHONE_PLACEHOLDER}
                        value={publicPhone}
                        onChange={(e) => setPublicPhone(e.target.value)}
                      />
                    </Field>
                    <Field label={ONBOARDING.PUBLIC_EMAIL_LABEL} optional error={fieldErrors.publicEmail ? "Email inválido" : undefined}>
                      <input
                        className={"ob-input" + (fieldErrors.publicEmail ? " err" : "")}
                        type="email"
                        placeholder={ONBOARDING.PUBLIC_EMAIL_PLACEHOLDER}
                        value={publicEmail}
                        onChange={(e) => setPublicEmail(e.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              </>
            )}

            {/* ─── Step 1: Configuración ──────────────────────────── */}
            {activeStep === 1 && (
              <>
                <div className="ob-card-head">
                  <h2>¿Cómo son tus <em>consultas?</em></h2>
                  <p>Define dónde ejerces y cuánto dura cada sesión.</p>
                </div>

                <div className="ob-fields cols-2">
                  <Field label={ONBOARDING.COUNTRY_LABEL} required hint={ONBOARDING.COUNTRY_HELPER}>
                    <OBSelect
                      value={country}
                      onChange={(e) => {
                        const c = e.target.value;
                        setCountry(c);
                        if (COUNTRY_CURRENCY[c]) setCurrency(COUNTRY_CURRENCY[c]);
                      }}
                    >
                      {SPANISH_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </OBSelect>
                  </Field>
                  <Field label="Moneda" hint={ONBOARDING.CURRENCY_HELPER}>
                    <OBSelect value={currency} onChange={(e) => setCurrency(e.target.value)}>
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </OBSelect>
                  </Field>
                  <Field label="Duración de la sesión (min)" required error={fieldErrors.sessionDuration ? "Inválido" : undefined}>
                    <NumStep
                      value={sessionDuration}
                      onChange={setSessionDuration}
                      min={15}
                      max={480}
                      step={5}
                      error={fieldErrors.sessionDuration}
                    />
                  </Field>
                  <Field label="Tiempo entre citas (min)" hint={ONBOARDING.BUFFER_HELPER} error={fieldErrors.bufferTime ? "Inválido" : undefined}>
                    <NumStep
                      value={bufferTime}
                      onChange={setBufferTime}
                      min={0}
                      max={120}
                      step={5}
                      error={fieldErrors.bufferTime}
                    />
                  </Field>
                </div>
              </>
            )}

            {/* ─── Step 2: Servicios ───────────────────────────────── */}
            {activeStep === 2 && (
              <>
                <div className="ob-card-head">
                  <h2>¿Qué <em>servicios</em> ofreces?</h2>
                  <p>Agrega al menos un servicio con su precio. Por ejemplo: «Consulta general», «Consulta de seguimiento».</p>
                </div>

                {services.length > 0 && (
                  <div className="ob-svc-list">
                    {services.map((svc) => (
                      <div className="ob-svc-row added" key={svc.id}>
                        <div className="ob-svc-info">
                          <div className="nm">{svc.name}</div>
                          <div className="pr">
                            ${Number(svc.price).toLocaleString("es-MX")} {currency}
                            <span className="duration">· {sessionDuration} min</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ob-svc-del"
                          aria-label="Eliminar"
                          onClick={() => removeService(svc.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="ob-svc-input-group">
                  <Field label="Nombre del servicio" error={fieldErrors.svcName ? "Requerido" : undefined}>
                    <input
                      className={"ob-input" + (fieldErrors.svcName ? " err" : "")}
                      placeholder="Ej. Consulta general"
                      value={svcName}
                      onChange={(e) => setSvcName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddService(); }}
                    />
                  </Field>
                  <Field label={`Precio (${currency})`} error={fieldErrors.svcPrice ? "Inválido" : undefined}>
                    <input
                      className={"ob-input" + (fieldErrors.svcPrice ? " err" : "")}
                      inputMode="decimal"
                      placeholder="800"
                      value={svcPrice}
                      onChange={(e) => setSvcPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddService(); }}
                    />
                  </Field>
                  <div className="add-btn">
                    <button
                      type="button"
                      className="ob-btn outline-accent"
                      onClick={handleAddService}
                      disabled={svcSubmitting || !svcName.trim()}
                    >
                      <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Agregar
                    </button>
                  </div>
                </div>

                <div className="ob-loc-tele" style={{ marginTop: 18 }}>
                  <div className="icon">
                    {showPrices ? <Eye size={18} /> : <EyeOff size={18} />}
                  </div>
                  <div className="copy">
                    <div className="ttl">Mostrar precios en mi perfil público</div>
                    <div className="sub">
                      Tus pacientes verán los precios al confirmar la cita. Los registramos
                      para tu seguimiento de ingresos aunque los ocultes.
                    </div>
                  </div>
                  <button
                    type="button"
                    className={"ob-toggle" + (showPrices ? " on" : "")}
                    aria-pressed={showPrices}
                    onClick={() => setShowPrices((v) => !v)}
                  />
                </div>
              </>
            )}

            {/* ─── Step 3: Ubicación ───────────────────────────────── */}
            {activeStep === 3 && (
              <>
                <div className="ob-card-head">
                  <h2>¿Dónde <em>atiendes?</em></h2>
                  <p>Agrega al menos una ubicación. Puedes tener consultorios presenciales y también ofrecer teleconsulta.</p>
                </div>

                <div className="ob-loc-tele">
                  <div className="icon"><Video size={18} /></div>
                  <div className="copy">
                    <div className="ttl">Teleconsulta · videollamada</div>
                    <div className="sub">Atiende a tus pacientes en línea</div>
                  </div>
                  <button
                    type="button"
                    className={"ob-toggle" + (teleEnabled ? " on" : "")}
                    aria-pressed={teleEnabled}
                    disabled={locSubmitting}
                    onClick={handleToggleTele}
                  />
                </div>

                {locations.filter((l) => !l.isVirtual).length > 0 && (
                  <div className="ob-loc-saved">
                    {locations.filter((l) => !l.isVirtual).map((loc) => (
                      <div className="ob-loc-saved-row" key={loc.id}>
                        <div className="ic"><MapPin size={16} /></div>
                        <div className="info">
                          <div className="nm">{loc.name || loc.address}</div>
                          <div className="sub">
                            {[loc.address, loc.colonia, loc.city, loc.state].filter(Boolean).join(", ")}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ob-svc-del"
                          aria-label="Eliminar"
                          onClick={() => removeLocation(loc.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {locationLimitReached ? (
                  <div className="ob-callout" style={{ marginBottom: 0 }}>
                    <span className="ic">i</span>
                    <p>
                      <strong style={{ display: "block", marginBottom: 2, color: "var(--accent-deep)" }}>
                        {ONBOARDING.LOC_LIMIT_REACHED}
                      </strong>
                      {canTryProTrial ? ONBOARDING.LOC_LIMIT_TRY_TRIAL : ONBOARDING.LOC_LIMIT_UPGRADE_PRO}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="ob-loc-card">
                      <div className="ob-loc-card-head">
                        <span className="lbl">Consultorio {locations.filter((l) => !l.isVirtual).length + 1}</span>
                      </div>
                      <div className="ob-fields cols-2">
                        <Field label="Nombre" optional>
                          <input
                            className="ob-input"
                            placeholder="Ej. Consultorio principal"
                            value={locForm.name}
                            onChange={(e) => setLocForm({ ...locForm, name: e.target.value })}
                          />
                        </Field>
                        <Field label="Calle y número" required error={fieldErrors.locAddress ? "Requerido" : undefined}>
                          <input
                            className={"ob-input" + (fieldErrors.locAddress ? " err" : "")}
                            placeholder="Ej. Av. Hidalgo 123"
                            value={locForm.address}
                            onChange={(e) => setLocForm({ ...locForm, address: e.target.value })}
                          />
                        </Field>
                        <Field label="Colonia" optional>
                          <input
                            className="ob-input"
                            placeholder="Ej. Centro"
                            value={locForm.colonia}
                            onChange={(e) => setLocForm({ ...locForm, colonia: e.target.value })}
                          />
                        </Field>
                        <Field label="Ciudad" required error={fieldErrors.locCity ? "Requerido" : undefined}>
                          <input
                            className={"ob-input" + (fieldErrors.locCity ? " err" : "")}
                            value={locForm.city}
                            onChange={(e) => setLocForm({ ...locForm, city: e.target.value })}
                          />
                        </Field>
                        <Field label={country === "MX" ? "Estado" : "Estado / Provincia"} required error={fieldErrors.locState ? "Requerido" : undefined}>
                          {country === "MX" ? (
                            <OBSelect
                              value={locForm.state}
                              onChange={(e) => setLocForm({ ...locForm, state: e.target.value })}
                              error={fieldErrors.locState}
                            >
                              <option value="">Selecciona…</option>
                              {MEXICO_STATES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </OBSelect>
                          ) : (
                            <input
                              className={"ob-input" + (fieldErrors.locState ? " err" : "")}
                              value={locForm.state}
                              onChange={(e) => setLocForm({ ...locForm, state: e.target.value })}
                            />
                          )}
                        </Field>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="ob-btn outline-dash"
                      style={{ marginTop: 14 }}
                      onClick={() => handleAddLocation(false)}
                      disabled={locSubmitting}
                    >
                      <span className="plus">+</span> Agregar consultorio
                    </button>
                  </>
                )}
              </>
            )}

            {/* ─── Step 4: Horario ─────────────────────────────────── */}
            {activeStep === 4 && (
              <>
                <div className="ob-card-head">
                  <h2>¿Cuándo <em>atiendes?</em></h2>
                  <p>Configura el primer bloque y haz clic en «Continuar» (o en «Agregar horario» para añadir varios). Podrás ajustarlo después.</p>
                </div>

                {locations.some((l) => l.isVirtual) && (
                  <div className="ob-callout">
                    <span className="ic">i</span>
                    <p>{ONBOARDING.SCHEDULE_TELECONSULTA_INFO}</p>
                  </div>
                )}

                {schedules.length > 0 && (
                  <div className="ob-sched-saved">
                    {schedules.map((s) => {
                      const loc = locations.find((l) => l.id === s.locationId);
                      return (
                        <div className="ob-sched-saved-row" key={s.id}>
                          <div className="info">
                            <div className="ttl">
                              {SCHEDULE_STRINGS.DAYS[s.dayOfWeek]} · {s.startTime} – {s.endTime}
                            </div>
                            {loc && (
                              <div className="sub">
                                {loc.isVirtual ? "Teleconsulta" : (loc.name || loc.address || "Ubicación")}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="ob-svc-del"
                            aria-label="Eliminar"
                            onClick={() => deleteSlot(s.id)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="ob-schedule-block">
                  <div className="ob-schedule-block-head">
                    <span className="lbl">Bloque {schedules.length + 1}</span>
                  </div>
                  <div className="ob-fields">
                    <Field label="Día de la semana">
                      <OBSelect
                        value={String(scheduleForm.dayOfWeek)}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfWeek: Number(e.target.value) })}
                      >
                        {DAYS.map((d) => (
                          <option key={d.value} value={String(d.value)}>{d.label}</option>
                        ))}
                      </OBSelect>
                    </Field>
                    <div className="ob-time-row">
                      <Field label="Hora inicio" error={fieldErrors.startTime ? "Inválido" : undefined}>
                        <input
                          type="time"
                          className={"ob-input" + (fieldErrors.startTime ? " err" : "")}
                          value={scheduleForm.startTime}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, startTime: e.target.value })}
                        />
                      </Field>
                      <Field label="Hora fin" error={fieldErrors.endTime ? "Inválido" : undefined}>
                        <input
                          type="time"
                          className={"ob-input" + (fieldErrors.endTime ? " err" : "")}
                          value={scheduleForm.endTime}
                          onChange={(e) => {
                            setScheduleForm({ ...scheduleForm, endTime: e.target.value });
                            if (fieldErrors.endTime) setFieldErrors({});
                          }}
                        />
                      </Field>
                    </div>
                    {locations.length > 0 && (
                      <Field
                        label="Ubicación"
                        required
                        hint="Selecciona el consultorio o teleconsulta para este bloque."
                        error={fieldErrors.locationId ? "Selecciona una ubicación" : undefined}
                      >
                        <OBSelect
                          value={scheduleForm.locationId ? String(scheduleForm.locationId) : ""}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, locationId: e.target.value })}
                          error={fieldErrors.locationId}
                        >
                          <option value="">Selecciona…</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={String(loc.id)}>
                              {loc.isVirtual
                                ? (loc.name || "Teleconsulta")
                                : (loc.name || loc.address || "Consultorio")}
                            </option>
                          ))}
                        </OBSelect>
                      </Field>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="ob-btn outline-dash"
                  style={{ marginTop: 12 }}
                  onClick={handleAddSchedule}
                  disabled={scheduleSubmitting}
                >
                  <span className="plus">+</span> Agregar horario
                </button>

                <div className="ob-loc-tele" style={{ marginTop: 18 }}>
                  <div className="icon">
                    {showSchedule ? <Eye size={18} /> : <EyeOff size={18} />}
                  </div>
                  <div className="copy">
                    <div className="ttl">Mostrar mi horario semanal en mi perfil público</div>
                    <div className="sub">
                      Tu horario sigue siendo necesario para generar las citas disponibles.
                      Si lo ocultas, los pacientes solo verán fechas y horas para reservar.
                    </div>
                  </div>
                  <button
                    type="button"
                    className={"ob-toggle" + (showSchedule ? " on" : "")}
                    aria-pressed={showSchedule}
                    onClick={() => setShowSchedule((v) => !v)}
                  />
                </div>
              </>
            )}

            {/* ─── Step 5: ¡Listo! ─────────────────────────────────── */}
            {activeStep === 5 && (
              <>
                <div className="ob-success">
                  <div className="ob-success-icon">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <h2>¡Tu perfil está <em>listo!</em></h2>
                  <p>Empieza a compartirlo para recibir tus primeras citas.</p>
                </div>

                <div className="ob-share-card">
                  <div className="ob-share-eyebrow">Tu enlace de perfil</div>
                  <div className="ob-share-link">
                    <span className="url">{profileUrl}</span>
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className={copied ? "copied" : ""}
                    >
                      {copied ? (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Copiado
                        </>
                      ) : (
                        <><Copy size={14} /> Copiar</>
                      )}
                    </button>
                  </div>
                  <a
                    href={profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ob-btn outline-accent"
                    style={{ width: "100%" }}
                  >
                    <ExternalLink size={14} /> Ver mi perfil
                  </a>
                  <div className="ob-channels">
                    <button type="button" className="ch-whatsapp" onClick={handleWhatsAppShare}>
                      <MessageCircle size={14} /> WhatsApp
                    </button>
                    <button type="button" className="ch-facebook" onClick={handleFacebookShare}>
                      <Share2 size={14} /> Facebook
                    </button>
                    <button type="button" className="ch-x" onClick={handleTwitterShare}>
                      <Share2 size={14} /> X / Twitter
                    </button>
                    <button type="button" className="ch-instagram" onClick={handleInstagramShare}>
                      <Share2 size={14} /> Instagram
                    </button>
                  </div>
                </div>

                <div className="ob-qr">
                  <div ref={qrRef} className="ob-qr-img">
                    <QRCodeCanvas value={profileUrl} size={100} fgColor="#1A1C2E" />
                  </div>
                  <div className="ob-qr-info">
                    <div className="ttl">Código QR</div>
                    <p>Imprime o comparte este código para que tus pacientes accedan a tu perfil con solo escanearlo.</p>
                    <button type="button" className="ob-btn outline-ink" onClick={handleDownloadQR}>
                      <Download size={14} /> Descargar QR
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Sticky footer ───────────────────────────────────────────── */}
      <div className="ob-foot">
        <div className="ob-foot-inner">
          <button
            type="button"
            className="ob-back"
            onClick={() => {
              setActiveStep((s) => s - 1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            disabled={activeStep === 0 || saving}
          >
            ← Atrás
          </button>
          {hasSavedOnce && (
            <span className={"saved" + (saving ? " busy" : "")}>
              <span className="dot" />
              {saving ? "Guardando…" : "Guardado"}
            </span>
          )}
          {isLast ? (
            <button
              type="button"
              className="ob-btn primary"
              onClick={handleFinish}
              disabled={saving}
            >
              {saving ? "Guardando…" : "Ir a mi dashboard →"}
            </button>
          ) : (
            <button
              type="button"
              className="ob-btn primary"
              onClick={stepHandlers[activeStep]}
              disabled={saving}
            >
              {saving ? "Guardando…" : nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
